import React, { useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import MemberSearch from '@/components/matching/MemberSearch'
import { groupService } from '@/services/groupService'
import { useToast } from '@/contexts/ToastContext'
import { GroupRoom } from '@/types/group'
import { User } from '@/types/user'

interface CreateGroupModalProps {
  isOpen: boolean
  onClose: () => void
  onGroupCreated: (group: GroupRoom) => void
}

const CreateGroupModal: React.FC<CreateGroupModalProps> = ({
  isOpen,
  onClose,
  onGroupCreated
}) => {
  const { showToast } = useToast()

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [selectedMembers, setSelectedMembers] = useState<User[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  if (!isOpen) {
    return null
  }

  const validate = (): boolean => {
    const nextErrors: Record<string, string> = {}
    if (!name.trim()) {
      nextErrors.name = 'Group name is required'
    }
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleSelectMember = (user: User) => {
    setSelectedMembers((prev) =>
      prev.some((m) => m.id === user.id) ? prev : [...prev, user]
    )
  }

  const handleRemoveMember = (userId: string) => {
    setSelectedMembers((prev) => prev.filter((m) => m.id !== userId))
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!validate()) {
      return
    }

    try {
      setIsSubmitting(true)
      const group = await groupService.createGroup({
        name: name.trim(),
        ...(description.trim() && { description: description.trim() }),
        memberIds: selectedMembers.map((m) => m.id)
      })
      showToast({ type: 'success', message: 'Group created' })
      onGroupCreated(group)
      onClose()
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to create group'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-xl font-semibold text-gray-900">Create Group</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <div>
            <label htmlFor="group-name" className="block text-sm font-medium text-gray-700 mb-1">
              Group name *
            </label>
            <input
              id="group-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="e.g. Cohort 1"
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          <div>
            <label htmlFor="group-description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="group-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="What is this group for?"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Members</label>
            {selectedMembers.length > 0 && (
              <ul className="mb-2 flex flex-wrap gap-2">
                {selectedMembers.map((member) => (
                  <li
                    key={member.id}
                    className="flex items-center gap-1 bg-primary-50 text-primary-700 text-sm rounded-full px-3 py-1"
                  >
                    {member.firstName} {member.lastName}
                    <button
                      type="button"
                      aria-label={`Remove ${member.firstName} ${member.lastName}`}
                      onClick={() => handleRemoveMember(member.id)}
                      className="text-primary-500 hover:text-primary-700"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <MemberSearch onSelect={handleSelectMember} />
          </div>

          <div className="flex justify-end gap-2 border-t pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating…' : 'Create Group'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateGroupModal
